/* eslint-disable jsx-a11y/anchor-is-valid */
import { SEARCH_TYPE, LocationTarget } from '@bingads-webui-react/bing-maps';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import odata from '@bingads-webui-component-legacy/http/channel/odata';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { RadioGroup } from '@bingads-webui-react/radio';
import React, { useContext, useMemo, useCallback, useEffect, useRef, Fragment } from 'react';
import { PrimaryButton, DefaultButton, Stack, Link, Label, Checkbox, Toggle } from '@bingads-webui-react/fabric-control';
import * as languageBundle from '@bingads-webui-campaign-legacy/location-targets/util/languageBundle';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Disclaimer } from '@bingads-webui-campaign/hotel-common-upsert';
import { SpecificLocationTargetsEditor, ErrorString, withVerticalAlignCenterSpan, Errors, checkShouldShowErrors, locationTargetsOptionKeys } from '@bingads-webui-campaign-react/wizard-shared-components';
import * as locationTargetConverter from '@bingads-webui-campaign-legacy/location-targets/util/locationTargetConverter';
import { glyphIconStyle, convertToLocalStyle } from '@bingads-webui-campaign-react/common-styles';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { IncludedLocationGrid, ExcludedLocationGrid } from './grid';
import { validateLocations, locationErrors, getLocationErrorMsg, shouldNotHideInclude } from '../components/utility';

export const physicalIntentOptions = {
  PeopleIn: 'PeopleIn',
  PeopleSearchingForOrViewingPages: 'PeopleSearchingForOrViewingPages',
  PeopleInOrSearchingForOrViewingPages: 'PeopleInOrSearchingForOrViewingPages',
};

export function convertIntentOption(option) {
  if (option.peopleInLocation && option.peopleSearchingForLocation) {
    return physicalIntentOptions.PeopleInOrSearchingForOrViewingPages;
  } else if (option.peopleInLocation) {
    return physicalIntentOptions.PeopleIn;
  } else if (option.peopleSearchingForLocation) {
    return physicalIntentOptions.PeopleSearchingForOrViewingPages;
  }
  return physicalIntentOptions.PeopleInOrSearchingForOrViewingPages;
}

export function getLocationIntent(option) {
  switch (option) {
    case physicalIntentOptions.PeopleIn:
      return {
        peopleInLocation: true,
        peopleSearchingForLocation: false,
      };
    case physicalIntentOptions.PeopleSearchingForOrViewingPages:
      return {
        peopleInLocation: false,
        peopleSearchingForLocation: true,
      };
    case physicalIntentOptions.PeopleInOrSearchingForOrViewingPages:
    default:
      return {
        peopleInLocation: true,
        peopleSearchingForLocation: true,
      };
  }
}

export function patchForLocationTargetGrid(criterion) {
  const Criterion = criterion.CriterionType === 'Radius' ? criterion.Criterion.RadiusCriterion : criterion.Criterion.LocationCriterion;
  return {
    ...criterion,
    location: Criterion.NameTypeCombo,
    bidAdjustmentDropdown: criterion.Criterion.BidAdjustment >= 0 ? 'Increase by' : 'Decrease by',
    bidAdjustmentInput: criterion.Criterion.BidAdjustment == null ? 0 : Math.abs(criterion.Criterion.BidAdjustment),
  };
}

const RadisUnitMap = {
  mi: 'mi',
  km: 'km',
  Miles: 'mi',
  Kilometers: 'km',
};

export function getLocationHashWithStatus(criterion) {
  const Criterion = criterion.CriterionType === 'Radius' ? criterion.Criterion.RadiusCriterion : criterion.Criterion.LocationCriterion;
  return JSON.stringify([
    Criterion.LocationId,
    Criterion.Type,
    Criterion.CountryCode,
    Criterion.Latitude,
    Criterion.Longitude,
    criterion.SelectionStatus,
    Criterion.Radius,
    RadisUnitMap[Criterion.RadiusUnit],
  ]);
}

export function getLocationHash(criterion) {
  const Criterion = criterion.CriterionType === 'Radius' ? criterion.Criterion.RadiusCriterion : criterion.Criterion.LocationCriterion;
  return JSON.stringify({
    LocationId: Criterion.LocationId,
    Type: Criterion.Type,
    Name: Criterion.Name,
    CountryCode: Criterion.CountryCode,
    Latitude: Criterion.Latitude,
    Longitude: Criterion.Longitude,
  });
}

function fromBingMapTargetToLocationCriterion(data) {
  const isRadius = data.isRadiusLocationTarget;
  const isIncluded = data.targetingType === 'Included';
  const criterion = isRadius ? locationTargetConverter.buildLocationCriterionFromSearchResult(
    data.location,
    {
      mode: 'radius', radius: data.radiusSize, unit: data.radiusUnit, status: isIncluded ? 'Included' : 'Excluded',
    }
  ) : locationTargetConverter.buildLocationCriterionFromSearchResult(
    data.location,
    { mode: 'location', status: isIncluded ? 'Included' : 'Excluded' }
  );

  if (isIncluded) {
    criterion.SelectionStatus = 'Included';
  } else {
    criterion.SelectionStatus = 'Excluded';
  }
  criterion.Id = data.Id;

  return criterion;
}

function createIdAttributes(Ids) {
  if (Ids == null) return [];
  return Ids.map(Id => ({ Id }));
}

function fromLocationCriterionToBingMapTarget(data) {
  if (data) {
    // TO DO Need to check how to merge bulkupsert format to criterion odata format
    return _.chain(data)
      .filter(criterion => criterion.Criterion.Type !== 'LocationIntentOption')
      .map((criterion) => {
        const Criterion = criterion.CriterionType === 'Radius' ? criterion.Criterion.RadiusCriterion : criterion.Criterion.LocationCriterion;
        const isExcluded = criterion.SelectionStatus === 'Excluded';
        const ParentLocations = criterion.CriterionType === 'Radius' ? createIdAttributes(Criterion.LocationTargetEnclosingLocationIds) : createIdAttributes(Criterion.EnclosingLocationIds);
        const locationTarget =
        isExcluded ?
          LocationTarget.CreateExcludedLocationTarget({
            CountryCode: Criterion.CountryCode,
            ExpandedName: Criterion.Name,
            GeoPoint: Criterion.GeoPoint ? Criterion.GeoPoint : {
              Latitude: Criterion.Latitude,
              Longitude: Criterion.Longitude,
            },
            Id: Criterion.LocationId,
            ParentLocations,
            Type: Criterion.LocationTargetType || Criterion.LocationType || Criterion.Type,
          }, Criterion.Radius, RadisUnitMap[Criterion.RadiusUnit])
          : LocationTarget.CreateIncludedLocationTarget({
            CountryCode: Criterion.CountryCode,
            ExpandedName: Criterion.Name,
            GeoPoint: Criterion.GeoPoint ? Criterion.GeoPoint : {
              Latitude: Criterion.Latitude,
              Longitude: Criterion.Longitude,
            },
            Id: Criterion.LocationId,
            ParentLocations,
            Type: Criterion.LocationTargetType || Criterion.LocationType || Criterion.Type,
          }, Criterion.Radius, RadisUnitMap[Criterion.RadiusUnit]);
        locationTarget.Id = criterion.Id;
        return locationTarget;
      })
      .value();
  }

  return [];
}

export const LocationTargetsIntent = (props) => {
  const {
    data,
    setData,
    isLocationTargetingImprovementEnabled,
  } = props;

  const {
    peopleInLocation,
    peopleSearchingForLocation,
  } = data;

  const i18n = useContext(I18nContext);
  const peopleInLocationString = i18n.getString(_TL_('People in your targeted locations'));
  const peopleSearchingForLocationString = i18n.getString(_TL_('People searching for or viewing pages about your targeted locations'));
  const peopleInOrInterestedInLocationString = i18n.getString(_TL_('People in, searching for, or viewing webpages about your targeted locations'));

  // For user who only select original option "peopleSearchingForLocation", we will convert it to "peopleInOrInterestedInLocation" option
  if (isLocationTargetingImprovementEnabled && !peopleInLocation && peopleSearchingForLocation) {
    setData({ peopleInLocation: true, peopleSearchingForLocation: true });
  }

  const intentOptionsKeysMap = useMemo(() => ({
    [physicalIntentOptions.PeopleIn]: (
      <>
        <span>{peopleInLocationString}</span>
        <HelpPopup
          topic="pop_BA_CampaignCreate_Settings_PhysicalIntentOn"
          aria-label={i18n.getString(_TL_('Click to learn more about {{item}}'), { item: peopleInLocationString })}
        />
      </>
    ),
    [physicalIntentOptions.PeopleInOrSearchingForOrViewingPages]: (
      <>
        <span>{peopleInOrInterestedInLocationString}</span>
        <HelpPopup
          topic="pop_BA_CampaignCreate_Settings_PhysicalIntentOnly"
          aria-label={i18n.getString(_TL_('Click to learn more about {{item}}'), { item: peopleInOrInterestedInLocationString })}
        />
      </>
    ),
  }), [i18n, peopleInLocationString, peopleInOrInterestedInLocationString]);

  const intentOptionsKeys = useMemo(() => (_.compact([
    physicalIntentOptions.PeopleIn,
    physicalIntentOptions.PeopleInOrSearchingForOrViewingPages,
  ])), []);

  const checkedRadio = peopleSearchingForLocation ?
    physicalIntentOptions.PeopleInOrSearchingForOrViewingPages :
    physicalIntentOptions.PeopleIn;

  const onIntentOptionChanged = useCallback((key) => {
    if (key === physicalIntentOptions.PeopleIn) {
      setData({
        peopleInLocation: true,
        peopleSearchingForLocation: false,
      });
    } else {
      setData({
        peopleInLocation: true,
        peopleSearchingForLocation: true,
      });
    }
  }, [setData]);

  return (
    <div className={classNames('intent-target')}>
      <div className={classNames('title')}>
        <Label>{i18n.getString(_TL_('Who should see your ads?'))}</Label>
      </div>
      <div className={classNames('intent-target-options')}>
        {isLocationTargetingImprovementEnabled ?
          <ul role="radiogroup" aria-label={i18n.getString(_TL_('Who should see your ads?'))}>
            <RadioGroup
              items={intentOptionsKeys}
              renderItem={key => intentOptionsKeysMap[key]}
              onChange={onIntentOptionChanged}
              checked={checkedRadio}
              getItemId={_.identity}
            />
          </ul> :
          <Stack tokens={{ childrenGap: 8 }}>
            <Checkbox
              className="peopleIn"
              label={peopleInLocationString}
              checked={peopleInLocation}
              onChange={(ev, isChecked) => {
                setData({ peopleInLocation: isChecked });
              }}
              onRenderLabel={withVerticalAlignCenterSpan(() => (<HelpPopup
                topic="pop_BA_CampaignCreate_Settings_PhysicalIntentOn"
                aria-label={i18n.getString(_TL_('Click to learn more about {{item}}'), { item: peopleInLocationString })}
              />))}
            />
            <Checkbox
              className="peopleSearchingFor"
              label={peopleSearchingForLocationString}
              checked={peopleSearchingForLocation}
              onChange={(ev, isChecked) => {
                setData({ peopleSearchingForLocation: isChecked });
              }}
              onRenderLabel={withVerticalAlignCenterSpan(() => (<HelpPopup
                topic="pop_BA_CampaignCreate_Settings_PhysicalIntentOnly"
                aria-label={i18n.getString(_TL_('Click to learn more about {{item}}'), { item: peopleSearchingForLocationString })}
              />))}
            />
            {!peopleInLocation && !peopleSearchingForLocation &&
            <div role="alert" aria-live="polite">
              <ErrorString error={i18n.getString(_TL_('At least one intent option must be selected'))} />
            </div>}
          </Stack>
        }
      </div>
    </div>);
};

LocationTargetsIntent.propTypes = {
  setData: PropTypes.func.isRequired,
  data: PropTypes.shape({}),
  isLocationTargetingImprovementEnabled: PropTypes.bool.isRequired,
};

LocationTargetsIntent.defaultProps = {
  data: {},
};

function getIncludedLocations(locations) {
  if (locations == null) return [];
  return locations.filter(location => location.SelectionStatus === 'Included');
}

function getExcludedLocations(locations) {
  if (locations == null) return [];
  return locations.filter(location => location.SelectionStatus === 'Excluded');
}

function fillAdditionalInfoToCriterion(location) {
  const isPositive = location.bidAdjustmentDropdown === 'Increase by';
  // eslint-disable-next-line no-param-reassign
  location.Criterion.BidAdjustment = isPositive ? location.bidAdjustmentInput : -location.bidAdjustmentInput;
}

export function fillAdditionalInfoToBundleLocationCriterions(currentLocations, bundleLocationCriterions) {
  if (bundleLocationCriterions) {
    return _.chain(bundleLocationCriterions)
      .map((target) => {
        const criterion = { ...target };
        const hash = getLocationHash(criterion);
        const currentLocation = currentLocations.find(current => getLocationHash(current) === hash);
        if (currentLocation) {
          criterion.Criterion.BidAdjustment = currentLocation.Criterion.BidAdjustment;
          criterion.Criterion.OriginalBidAdjustment = currentLocation.Criterion.OriginalBidAdjustment;
        }
        return patchForLocationTargetGrid(criterion);
      })
      .value();
  }
  return [];
}

function fillAdditionalInfoToGrid(currentLocations, newLocations) {
  if (newLocations) {
    return _.chain(newLocations)
      .map((locationTarget) => {
        const isIncluded = locationTarget.targetingType === 'Included';
        const criterion = fromBingMapTargetToLocationCriterion(locationTarget);
        criterion.SelectionStatus = isIncluded ? 'Included' : 'Excluded';
        const hash = getLocationHash(criterion);
        const currentLocation = currentLocations.find(current => getLocationHash(current) === hash);
        if (currentLocation) {
          criterion.Criterion.BidAdjustment = currentLocation.Criterion.BidAdjustment;
          criterion.Criterion.OriginalBidAdjustment = currentLocation.Criterion.OriginalBidAdjustment;
        }
        return patchForLocationTargetGrid(criterion);
      })
      .value();
  }

  return [];
}

const LocationTargetsEditor = (props) => {
  const i18n = useContext(I18nContext);
  const {
    data,
    setData,
    isAdGroupLevel,
    toggleBidModeAndSetData,
    languageBundleString,
    bundleLocationCriterions,
    bidStrategyType,
    allowBidAdjustment,
    allowNegativeLocationTargeting,
    showSearchTypeOptions,
    showBulkLocations,
    strings,
    isBrandAwarenessCampaignScenario,
    defaultSelectedSearchType,
    showIntent,
    isLocationTargetingImprovementEnabled,
    isDomesticSimplifiedChineseEnabled,
    dealInfo,
  } = props;

  const backUpData = useRef({});

  useEffect(() => {
    backUpData.current = _.deepClone(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const myData = useMemo(() => ({
    includedLocations: shouldNotHideInclude(bidStrategyType) ? fromLocationCriterionToBingMapTarget(getIncludedLocations(data.currentLocations)) : [],
    excludedLocations: fromLocationCriterionToBingMapTarget(getExcludedLocations(data.currentLocations)),
    targetOption: data.targetOption,
    peopleInLocation: data.peopleInLocation,
    peopleSearchingForLocation: data.peopleSearchingForLocation,
  }), [bidStrategyType, data.currentLocations, data.peopleInLocation, data.peopleSearchingForLocation, data.targetOption]);

  const mySetData = useCallback((partialUpdate) => {
    const newData = {
      ...myData,
      ...partialUpdate,
    };

    const {
      excludedLocations = [],
      peopleInLocation,
      peopleSearchingForLocation,
    } = newData;

    let {
      includedLocations = [],
    } = newData;

    const {
      currentLocations = [],
    } = data;

    if (!shouldNotHideInclude(bidStrategyType)) {
      includedLocations = fromLocationCriterionToBingMapTarget(getIncludedLocations(data.currentLocations));
    }

    const newLocations = fillAdditionalInfoToGrid(currentLocations, includedLocations).concat(fillAdditionalInfoToGrid(currentLocations, excludedLocations));

    setData({
      targetOption: newData.targetOption,
      currentLocations: newLocations,
      peopleInLocation,
      peopleSearchingForLocation,
    });
  }, [data, myData, setData, bidStrategyType]);

  const targetOptionsKeysMap = useMemo(() => {
    const keysMap = {
      [locationTargetsOptionKeys.everywhere]: i18n.getString(_TL_('All available countries/regions')),
      [locationTargetsOptionKeys.specific]: i18n.getString(_TL_('Let me choose specific locations')),
    };
    if (shouldNotHideInclude(bidStrategyType)) {
      keysMap[locationTargetsOptionKeys.bundleOrDefaultMarket] = languageBundleString;
      return keysMap;
    }
    return keysMap;
  }, [i18n, languageBundleString, bidStrategyType]);

  const targetOptionsKeys = useMemo(() => (_.compact([
    shouldNotHideInclude(bidStrategyType) && locationTargetsOptionKeys.bundleOrDefaultMarket,
    !isDomesticSimplifiedChineseEnabled && locationTargetsOptionKeys.everywhere,
    locationTargetsOptionKeys.specific,
  ])), [bidStrategyType, isDomesticSimplifiedChineseEnabled]);

  const done = () => {
    const valid = validateLocations({
      currentLevelSettings: data,
    }, bidStrategyType);

    if (valid && valid !== locationErrors.BidIsNotValid) {
      return;
    }
    const isBundle = data.targetOption === locationTargetsOptionKeys.bundleOrDefaultMarket;
    const isTargetEverywhere = data.currentLocations == null || data.currentLocations.length === 0;
    if (isTargetEverywhere && !isBundle) {
      toggleBidModeAndSetData({
        targetOption: locationTargetsOptionKeys.everywhere,
        currentLocations: data.currentLocations,
      });
    } else {
      toggleBidModeAndSetData({
        currentLocations: isBundle ? fillAdditionalInfoToBundleLocationCriterions(data.currentLocations, bundleLocationCriterions) : data.currentLocations,
      });
    }
  };

  const cancel = () => {
    toggleBidModeAndSetData({
      targetOption: backUpData.current.targetOption,
      currentLocations: backUpData.current.currentLocations,
      peopleInLocation: backUpData.current.peopleInLocation,
      peopleSearchingForLocation: backUpData.current.peopleSearchingForLocation,
    });
  };

  const onTargetOptionChanged = (key) => {
    if (key === locationTargetsOptionKeys.everywhere) {
      mySetData({
        targetOption: key,
        peopleInLocation: true,
        peopleSearchingForLocation: true,
      });
    } else {
      mySetData({ targetOption: key });
    }
  };

  const {
    targetOption,
  } = myData;

  const showSpecificLocationTargetEditor = targetOption === locationTargetsOptionKeys.specific || isAdGroupLevel;
  const error = getLocationErrorMsg({ code: validateLocations({ currentLevelSettings: data }, bidStrategyType, dealInfo), i18n, dealInfo });

  return (
    <div className="location-target-options">
      {!shouldNotHideInclude(bidStrategyType) && <Disclaimer
        message={i18n.getString(_TL_('Only excluded locations are supported'))}
      />}
      <div className="header"><Label>{i18n.getString(_TL_('Where do you want your ads to appear?'))}</Label></div>
      {
        (!isAdGroupLevel) && (
          <div className="choiceGroup">
            <ul role="radiogroup" aria-label={i18n.getString(_TL_('Where do you want your ads to appear?'))}>
              <RadioGroup
                items={targetOptionsKeys}
                renderItem={key => targetOptionsKeysMap[key]}
                onChange={onTargetOptionChanged}
                checked={targetOption || locationTargetsOptionKeys.everywhere}
                getItemId={_.identity}
              />
            </ul>
          </div>
        )
      }
      <ul>
        {showSpecificLocationTargetEditor && <SpecificLocationTargetsEditor
          allowNegativeLocationTargeting={allowNegativeLocationTargeting}
          showSearchTypeOptions={showSearchTypeOptions}
          showBulkLocations={showBulkLocations}
          strings={strings}
          defaultSelectedSearchType={defaultSelectedSearchType}
          isBrandAwarenessCampaignScenario={isBrandAwarenessCampaignScenario}
          data={myData}
          setData={mySetData}
          scope={props.scope}
          edmCampaign={edmCampaign}
          allowPositiveLocationTargeting={shouldNotHideInclude(bidStrategyType)}
        />}
      </ul>
      {error && <div className="error"><ErrorString error={error} /></div>}
      <div>
        {showIntent && <LocationTargetsIntent data={myData} setData={mySetData} isLocationTargetingImprovementEnabled={isLocationTargetingImprovementEnabled} />}
      </div>
      {allowBidAdjustment &&
        <div className="footer">
          <PrimaryButton text={i18n.getString(_TL_('Done'))} onClick={done} />
          <DefaultButton text={i18n.getString(_TL_('Cancel'))} onClick={cancel} />
        </div>}
    </div>
  );
};

LocationTargetsEditor.propTypes = {
  setData: PropTypes.func.isRequired,
  toggleBidModeAndSetData: PropTypes.func.isRequired,
  data: PropTypes.shape({}),
  scope: PropTypes.shape({
    values: PropTypes.shape({
      cid: PropTypes.number.isRequired,
      aid: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  bundleLocationCriterions: PropTypes.shape([]).isRequired,
  languageBundleString: PropTypes.string,
  bidStrategyType: PropTypes.string.isRequired,
  isAdGroupLevel: PropTypes.bool,
  allowBidAdjustment: PropTypes.bool,
  allowNegativeLocationTargeting: PropTypes.bool,
  showSearchTypeOptions: PropTypes.bool,
  showBulkLocations: PropTypes.bool,
  strings: PropTypes.shape({}),
  isBrandAwarenessCampaignScenario: PropTypes.bool,
  defaultSelectedSearchType: PropTypes.oneOf(_.values(SEARCH_TYPE)),
  showIntent: PropTypes.bool.isRequired,
  isLocationTargetingImprovementEnabled: PropTypes.bool.isRequired,
  isDomesticSimplifiedChineseEnabled: PropTypes.bool.isRequired,
  dealInfo: PropTypes.shape({
    isForDeal: PropTypes.bool,
    dealCountryCode: PropTypes.string,
    dealSupportedLocationType: PropTypes.arrayOf(PropTypes.string),
  }),
};

LocationTargetsEditor.defaultProps = {
  data: {
  },
  languageBundleString: 'Canada, United States',
  isAdGroupLevel: false,
  allowBidAdjustment: true,
  allowNegativeLocationTargeting: true,
  showSearchTypeOptions: true,
  showBulkLocations: false,
  strings: {},
  isBrandAwarenessCampaignScenario: false,
  defaultSelectedSearchType: SEARCH_TYPE.Boundary,
  dealInfo: {},
};

const LocationTargetsGridContainer = (props) => {
  const {
    data,
    setData,
    bidStrategyType,
    showIntent,
  } = props;

  const i18n = useContext(I18nContext);

  const {
    currentLevelSettings = {},
    parentLevelSettings = {},
    useParentLevel,
  } = data;

  const myData = useMemo(() => ({
    peopleInLocation: currentLevelSettings.peopleInLocation,
    peopleSearchingForLocation: currentLevelSettings.peopleSearchingForLocation,
  }), [currentLevelSettings.peopleInLocation, currentLevelSettings.peopleSearchingForLocation]);

  const error = getLocationErrorMsg({ code: validateLocations(data, bidStrategyType), i18n });

  return (
    <div className="location-targets-grids">
      <Stack>
        {shouldNotHideInclude(bidStrategyType) && (
          <IncludedLocationGrid
            data={{
              currentLevelSettings: getIncludedLocations(currentLevelSettings.currentLocations),
              parentLevelSettings: getIncludedLocations(parentLevelSettings.currentLocations),
              useParentLevel,
            }}
            setData={(newIncluded) => {
              newIncluded.currentLevelSettings.forEach(fillAdditionalInfoToCriterion);
              setData({
                currentLocations: newIncluded.currentLevelSettings.concat(getExcludedLocations(currentLevelSettings.currentLocations)),
              });
            }}
          />
        )}
        <ExcludedLocationGrid
          data={{
            currentLevelSettings: getExcludedLocations(currentLevelSettings.currentLocations),
            parentLevelSettings: getExcludedLocations(parentLevelSettings.currentLocations),
            useParentLevel,
          }}
          setData={newExcluded => setData({
            currentLocations: newExcluded.currentLevelSettings.concat(getIncludedLocations(currentLevelSettings.currentLocations)),
          })}
        />
        {showIntent && <LocationTargetsIntent data={myData} setData={setData} isLocationTargetingImprovementEnabled />}
        {error && <div className="error"><ErrorString error={error} /></div>}
      </Stack>
    </div>
  );
};

LocationTargetsGridContainer.propTypes = {
  scope: PropTypes.shape({
    values: PropTypes.shape({
      cid: PropTypes.number.isRequired,
      aid: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  data: PropTypes.shape({
    currentLevelSettings: PropTypes.shape({}),
  }),
  setData: PropTypes.func.isRequired,
  bidStrategyType: PropTypes.string.isRequired,
  showIntent: PropTypes.bool.isRequired,
};

LocationTargetsGridContainer.defaultProps = {
  data: {
    currentLevelSettings: {
      targetOption: locationTargetsOptionKeys.everywhere,
    },
    useParentLevel: true,
  },
};


const getStyles = (__, core) => ({
  LocationTargetSetting: {
    ...convertToLocalStyle(glyphIconStyle(core)),
    '& .col-icon': {
      fontSize: '16px',
    },
    '& .everywhere': {
      '& .content': {
        marginRight: '8px',
      },
    },
    '& .edit-location-targets-button': {
      textDecoration: 'underline',
    },
    '& .location-target-options': {
      '& .choiceGroup': {
        '& label': {
          fontWeight: '400',
          padding: 'unset',
        },
        '& .radio ~ .radio': {
          marginTop: '8px',
        },
        '& > ul': {
          paddingLeft: 0,
        },
        marginLeft: '20px',
        marginTop: '8px',
      },
      '& .footer': {
        marginTop: '16px',
      },
      '& .ms-Button--primary': {
        marginRight: '8px',
      },
    },
    '& .specific-location-targets-editor': {
      marginLeft: '26px',
      marginTop: '4px',
      marginBottom: '16px',
    },
    '& .error': {
      marginTop: '8px',
    },
  },
});

export function getBundleCountries(language, market) {
  let bundleCountries = languageBundle.getBundle(language, market);

  if (!bundleCountries) {
    bundleCountries = [];
    let publisherCountry = languageBundle.getPublisherCountryByCode(market);

    if (_.isUndefined(publisherCountry)) {
      publisherCountry = {
        Country: 'US',
        LocationId: 190,
      };
    }

    bundleCountries.push(publisherCountry);
  }
  return bundleCountries;
}

const globalI18n = window.globalLegacyI18n.get('@bingads-webui/i18n-amd-loader!campaignui/location-targets');

export function getLanguageBundleString(bundleCountries) {
  const bundleCountryNames = [];
  _.each(bundleCountries, (country) => {
    const countryName = globalI18n.getString(`PublisherCountry_${country.Country}`);

    bundleCountryNames.push(countryName);
  });
  return bundleCountryNames.join(', ');
}

export function shouldSelectBundleOption(bundleCountries, locations) {
  // if there is no bundle found, return false
  if (!bundleCountries) {
    return false;
  }

  // if number of locations are not equal to number of bundle countries, return false
  if (bundleCountries.length !== locations.length) {
    return false;
  }

  for (let i = 0; i < locations.length; i += 1) {
    const location = locations[i];

    // if there is any radius/excluded target defined, we're not in bundle mode
    if (location.Criterion.Type === 'Radius' ||
      location.SelectionStatus === 'Excluded') {
      return false;
    } else if (!_.find(bundleCountries, country => country.LocationId === location.Criterion.LocationCriterion.LocationId)) { // if the location is not in the bundle list, return false
      return false;
    }
  }
  return true;
}

export function getBundleCountriesLocations(bundleCountries, lcid, cid, aid) {
  const locationIds = _.pluck(bundleCountries, 'LocationId');
  if (_.size(locationIds) === 0) {
    return Promise.resolve([]);
  }

  const reqs = _.map(locationIds, id => ({
    type: 'GET',
    url: `${edmCampaign.Customers.$withKey(cid).Accounts.$withKey(aid).Locations.$withKey(id).path}?%24expand=ParentLocations`,
    headers: lcid ? {
      lcid,
    } : undefined,
  }));

  return odata.batch({ reqs, needContentType: true });
}

export function getBundleCountriesAndBundleLocationCriterions(cid, aid, lcid, language, market) {
  const bundleCountries = getBundleCountries(language, market);
  return new Promise(resolve => getBundleCountriesLocations(bundleCountries, lcid, cid, aid).then((response) => {
    const bundleLocationCriterions = [];
    if (response.message) {
      throw new Error(response.message);
    }
    _.each(response, (location) => {
      bundleLocationCriterions.push(locationTargetConverter.buildLocationCriterionFromSearchResult(
        location.data,
        { mode: 'location', status: 'Included' }
      ));
    });
    console.log(response);
    resolve({ bundleCountries, bundleLocationCriterions });
  }).catch((error) => {
    console.log(error);
    resolve({ bundleCountries: [], bundleLocationCriterions: [] });
  }));
}

export const isTargetEverywhere = (settings, isAdGroupLevel, bidStrategyType) => {
  const {
    targetOption,
    currentLocations = [],
  } = settings;

  if (!shouldNotHideInclude(bidStrategyType)) {
    const excludedLocations = getExcludedLocations(currentLocations);
    return excludedLocations.length === 0;
  }

  if (isAdGroupLevel) {
    return currentLocations.length === 0;
  }
  return targetOption === locationTargetsOptionKeys.everywhere;
};

function compareLocationTargetArrays(initLocations, currentLocations) {
  const changes = { Added: [], Deleted: [] };

  // convert arrays into dictionaries, with hashcode as key
  const initDict = _.object(_.map(initLocations, lt => [getLocationHashWithStatus(lt), lt]));
  const currentDict = _.object(_.map(currentLocations, lt => [getLocationHashWithStatus(lt), lt]));

  // calculate newly added location targets
  _.each(currentDict, (currentTarget, hashCode) => {
    if (!_.has(initDict, hashCode)) {
      if (_.isUndefined(currentTarget.Id)) {
        // no Id, this is a new location target
        changes.Added.push(currentTarget);
      } else {
        // changed from radius to location or location to radius or radius changed for the same location
        changes.Added.push(_.omit(currentTarget, 'Id'));
      }
    } else if (currentTarget.Criterion.BidAdjustment !== currentTarget.Criterion.OriginalBidAdjustment) {
      if (_.isUndefined(currentTarget.Id)) {
        const currentHash = getLocationHashWithStatus(currentTarget);
        const initTarget = initLocations.find(x => getLocationHashWithStatus(x) === currentHash);
        if (initTarget) {
          // eslint-disable-next-line no-param-reassign
          currentTarget.Id = initTarget.Id;
        }
      }
      changes.Added.push(currentTarget);
    }
  });

  // calculate deleted location targets
  _.each(initDict, (initTarget, hashCode) => {
    if (!_.has(currentDict, hashCode)) {
      changes.Deleted.push(initTarget);
    }
  });

  return changes;
}

export function compareAndProcessLocations(initLocations, currentLocations) {
  let finalLocations = currentLocations;
  const changes = compareLocationTargetArrays(initLocations, finalLocations);

  finalLocations = _.map(changes.Deleted, locationTarget => ({
    ...locationTarget,
    Status: 'Deleted',
  })).concat(changes.Added);

  finalLocations = _.map(finalLocations, (finalLocation) => {
    let locationToSend = finalLocation;

    if (locationToSend.Criterion.Type === 'Radius') {
      locationToSend.CriterionType = locationToSend.Criterion.Type;
      if (locationToSend.Criterion.RadiusCriterion.IsOriginalNameEmpty) {
        locationToSend.Criterion.RadiusCriterion.Name = null;
      }
    } else {
      locationToSend.CriterionType = locationToSend.SelectionStatus === 'Included' ? 'Location' : 'NegativeLocation';
    }

    // For those not created in MT, it should be zero.
    if (locationToSend.Id < 0) {
      locationToSend = _.omit(locationToSend, 'Id');
    }
    locationToSend = _.omit(locationToSend, 'location', 'bidAdjustmentDropdown', 'bidAdjustmentInput', 'Campaign', 'LesLocationType');
    return locationToSend;
  });

  return finalLocations;
}

export function parseLocationTargets(rawLocations, level, Id) {
  const locations = rawLocations
    .filter(criterion => criterion.Criterion.Type === 'Radius' || criterion.Criterion.Type === 'Location')
    .map(criterion => patchForLocationTargetGrid(locationTargetConverter.buildLocationCriterionFromBiddableCampaignCriterion(criterion, level, Id)));
  const intent = rawLocations.find(criterion => criterion.Criterion.Type === 'LocationIntentOption');
  const intentId = intent ? intent.Id : null;
  const intentOption = intent ? getLocationIntent(intent.Criterion.IntentOption) : null;

  return {
    locations,
    intentId,
    intentOption,
  };
}

const InnerLocationTargetSettingWithoutError = (props) => {
  const {
    data,
    setData,
    bundleCountries,
    bundleLocationCriterions,
    isAdGroupLevel,
    bidStrategyType,
    allowBidAdjustment,
    allowNegativeLocationTargeting,
    showSearchTypeOptions,
    showBulkLocations,
    strings,
    isBrandAwarenessCampaignScenario,
    defaultSelectedSearchType,
    hideIntent,
    dealInfo,
    permissions = {},
  } = props;

  const {
    currentLevelSettings = {},
    parentLevelSettings = {},
    useParentLevel,
    bidMode,
  } = data;

  const isLocationTargetingImprovementEnabled = _.result(permissions.dynamic, 'LocationTargetingImprovement', false);
  const isDomesticSimplifiedChineseEnabled = _.result(permissions, 'IsDomesticSimplifiedChineseEnabled', false);

  const classes = useDefaultStyles(getStyles);

  const mySetLocationTargets = useCallback((partialUpdate) => {
    setData({
      ...data,
      currentLevelSettings: {
        ...data.currentLevelSettings,
        ...partialUpdate,
      },
    });
  }, [data, setData]);

  const toggleBidModeAndSetLocationTargets = useCallback((partialUpdate) => {
    setData({
      ...data,
      currentLevelSettings: {
        ...data.currentLevelSettings,
        ...partialUpdate,
      },
      bidMode: !bidMode,
    });
  }, [data, setData, bidMode]);

  const i18n = useContext(I18nContext);

  const toggleBidMode = useCallback(() => {
    setData({
      ...data,
      bidMode: !bidMode,
    });
  }, [setData, data, bidMode]);

  const turnOffBidAndToggleParentLevel = useCallback(() => {
    setData({
      ...data,
      useParentLevel: !useParentLevel,
      bidMode: false,
    });
  }, [setData, data, useParentLevel]);

  useEffect(() => {
    if (shouldNotHideInclude(bidStrategyType)) {
      // Check conflict and reset Data
      const includedLocations = getIncludedLocations(currentLevelSettings.currentLocations);
      const includedLocationsHash = includedLocations.map(getLocationHash);
      let excludedLocations = getExcludedLocations(currentLevelSettings.currentLocations);
      const excludedLocationsHash = excludedLocations.map(getLocationHash);
      if (!_.isEmpty(_.intersection(includedLocationsHash, excludedLocationsHash))) {
        excludedLocations = excludedLocations.filter(x => !includedLocationsHash.includes(getLocationHash(x)));
        mySetLocationTargets({
          ...currentLevelSettings,
          currentLocations: includedLocations.concat(excludedLocations),
        });
      }
    }
  }, [bidStrategyType, currentLevelSettings, mySetLocationTargets]);

  const languageBundleString = useMemo(() => getLanguageBundleString(bundleCountries), [bundleCountries]);

  const { targetOption } = currentLevelSettings;
  const showIntent = !hideIntent && shouldNotHideInclude(bidStrategyType) && (targetOption === locationTargetsOptionKeys.specific || targetOption === locationTargetsOptionKeys.bundleOrDefaultMarket || isAdGroupLevel);

  if (useParentLevel) {
    if (isAdGroupLevel) {
      // Show Campaign Data
      return (
        <div className={classNames('location-target-settings', 'location-target-settings--use-adgroup-level', classes.LocationTargetSetting)}>
          {isTargetEverywhere(parentLevelSettings, isAdGroupLevel) ? (
            <div className="everywhere">
              <span className="content">{i18n.getString(_TL_('Targeting everywhere'))}</span>
            </div>
          ) : (
            <div className="specific">
              <Stack>
                <LocationTargetsGridContainer data={data} setData={mySetLocationTargets} />
              </Stack>
            </div>)}
        </div>
      );
    }
    return (
      <div className={classNames('location-target-settings', 'location-target-settings--use-campaign-level', classes.LocationTargetSetting)}>
        <div className="everywhere">
          <span className="content">{i18n.getString(_TL_('Targeting everywhere'))}</span>
          <Link className="edit-location-targets-button" onClick={turnOffBidAndToggleParentLevel}>
            {i18n.getString(_TL_('Edit location targets'))}
          </Link>
        </div>
      </div>
    );
  }

  // target editor mode
  if (!bidMode) {
    return (
      <div className={classNames('location-target-settings', 'location-target-settings--edit-mode', classes.LocationTargetSetting)}>
        <LocationTargetsEditor
          scope={props.scope}
          bundleLocationCriterions={bundleLocationCriterions}
          languageBundleString={languageBundleString}
          allowNegativeLocationTargeting={allowNegativeLocationTargeting}
          showSearchTypeOptions={showSearchTypeOptions}
          strings={strings}
          isBrandAwarenessCampaignScenario={isBrandAwarenessCampaignScenario}
          defaultSelectedSearchType={defaultSelectedSearchType}
          data={currentLevelSettings}
          setData={mySetLocationTargets}
          toggleBidModeAndSetData={toggleBidModeAndSetLocationTargets}
          isAdGroupLevel={isAdGroupLevel}
          bidStrategyType={bidStrategyType}
          allowBidAdjustment={allowBidAdjustment}
          showIntent={showIntent}
          isLocationTargetingImprovementEnabled={isLocationTargetingImprovementEnabled}
          isDomesticSimplifiedChineseEnabled={isDomesticSimplifiedChineseEnabled}
          showBulkLocations={showBulkLocations}
          dealInfo={dealInfo}
        />
      </div>
    );
  }

  // bidMode with grid
  return (
    <div className={classNames('location-target-settings', 'location-target-settings--edit-mode--with-bids', classes.LocationTargetSetting)}>
      {isTargetEverywhere(currentLevelSettings, isAdGroupLevel, bidStrategyType) ? (
        <div className="everywhere">
          <span className="content">{i18n.getString(_TL_('Targeting everywhere'))}</span>
          <Link className="edit-location-targets-button" onClick={toggleBidMode}>
            {i18n.getString(_TL_('Edit location targets'))}
          </Link>
        </div>
      ) : (
        <div className="specific">
          <Stack>
            <Link className="edit-location-targets-button" onClick={toggleBidMode}>
              {i18n.getString(_TL_('Edit location targets'))}
            </Link>
            <LocationTargetsGridContainer
              data={data}
              setData={mySetLocationTargets}
              bidStrategyType={bidStrategyType}
              showIntent={isLocationTargetingImprovementEnabled && showIntent}
            />
          </Stack>
        </div>)}
    </div>
  );
};

InnerLocationTargetSettingWithoutError.propTypes = {
  scope: PropTypes.shape({
    values: PropTypes.shape({
      cid: PropTypes.number.isRequired,
      aid: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  data: PropTypes.shape({
    currentLevelSettings: PropTypes.shape({}),
  }),
  bundleCountries: PropTypes.shape([]).isRequired,
  bundleLocationCriterions: PropTypes.shape([]).isRequired,
  setData: PropTypes.func.isRequired,
  isAdGroupLevel: PropTypes.bool,
  bidStrategyType: PropTypes.string.isRequired,
  allowBidAdjustment: PropTypes.bool,
  allowNegativeLocationTargeting: PropTypes.bool,
  showSearchTypeOptions: PropTypes.bool,
  showBulkLocations: PropTypes.bool,
  strings: PropTypes.shape({}),
  defaultSelectedSearchType: PropTypes.oneOf(_.values(SEARCH_TYPE)),
  isBrandAwarenessCampaignScenario: PropTypes.bool,
  hideIntent: PropTypes.bool,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      LocationTargetingImprovement: PropTypes.bool,
    }),
  }).isRequired,
  dealInfo: PropTypes.shape({
    isForDeal: PropTypes.bool,
    dealCountryCode: PropTypes.string,
    dealSupportedLocationType: PropTypes.arrayOf(PropTypes.string),
  }),
};

InnerLocationTargetSettingWithoutError.defaultProps = {
  data: {
    currentLevelSettings: {
      targetOption: locationTargetsOptionKeys.everywhere,
    },
    useParentLevel: false,
  },
  isAdGroupLevel: false,
  allowBidAdjustment: true,
  allowNegativeLocationTargeting: true,
  showSearchTypeOptions: true,
  showBulkLocations: false,
  strings: {},
  defaultSelectedSearchType: SEARCH_TYPE.Boundary,
  isBrandAwarenessCampaignScenario: false,
  hideIntent: false,
  dealInfo: {},
};

const LocationTargetSettingWithoutError = withPermissions(InnerLocationTargetSettingWithoutError);

export const LocationTargetSetting = (props) => {
  const {
    data,
    setData,
    showUserParentLevelToggle,
    keepInitValueWhenToggle,
  } = props;

  const {
    useParentLevel,
    currentLevelSettings,
  } = data || {};

  const {
    currentLocations, initLocations,
  } = currentLevelSettings || {};

  const i18n = useContext(I18nContext);

  const onToggle = useCallback((ev, checked) => {
    const noCheckedValue = keepInitValueWhenToggle ? initLocations : [];
    const updatedData = {
      ...data,
      useParentLevel: checked,
      currentLevelSettings: {
        ...currentLevelSettings,
        currentLocations: checked ? noCheckedValue : currentLocations,
        targetOption: checked ? locationTargetsOptionKeys.everywhere : locationTargetsOptionKeys.specific,
      },
    };

    setData({
      ...updatedData,
      errors: undefined,
    });
  }, [currentLevelSettings, currentLocations, data, initLocations, keepInitValueWhenToggle, setData]);

  return (
    <Fragment>
      <Stack tokens={{ childrenGap: 32 }}>
        {showUserParentLevelToggle &&
          <Toggle label={i18n.getString(_TL_('Use my campaign settings'))} defaultChecked={useParentLevel} onChange={onToggle} inlineLabel />
        }
        <LocationTargetSettingWithoutError
          {...props}
          setData={
          newData => setData({
            ...data,
            ...newData,
            errors: undefined,
          })
        }
        />
        {checkShouldShowErrors(data) && <Errors errors={data.errors} />}
      </Stack>
    </Fragment>
  );
};

LocationTargetSetting.propTypes = {
  data: PropTypes.shape({
    currentLevelSettings: PropTypes.shape({}),
    errors: PropTypes.arrayOf({}),
  }).isRequired,
  setData: PropTypes.func.isRequired,
  showUserParentLevelToggle: PropTypes.bool,
  keepInitValueWhenToggle: PropTypes.bool,
};

LocationTargetSetting.defaultProps = {
  showUserParentLevelToggle: false,
  keepInitValueWhenToggle: false,
};
